import {type AxiosInstance} from "axios";
import {createApi} from "../api/createApi.ts";
import {type UniqueNo} from "../model/core/UniqueNo.ts";
import {type ModifyAccountRequest} from "../model/user/api/ModifyAccountRequest.ts";
import {type PasswordChangeRequest} from "../model/user/api/PasswordChangeRequest.ts";
import {type MyAccountResponse} from "../model/user/api/MyAccountResponse.ts";
import {type UserCover, type UserCoversResponse, type UserDetails} from "../model/user/UserCover.ts";
import {type UserAuditTrail} from "../model/user/UserAuditTrail.ts";
import {enqueueSnackbar} from "notistack";
import {t} from "i18next";

const CURRENT_USER = "/user/me";
const USER_PASSWORD_OVERRIDE = "/user/password";
const USERS = "/users";
const USER_ASSEMBLY = "/user/assembly";
const USER_ADD = "/user/create";
const USER_DETAILS = "/user/details/";
const USER_ACTIVATE = "/user/activate/";
const USER_EDIT = "/user/edit/";
const USER_DEACTIVATE = "/user/deactivate/";
const USER_REMOVE = "/user/remove/";
const USER_AUDIT_TRAIL = "/user/auditTrail/";

export interface SaveUserRequest extends UserCover {
    roleNo: UniqueNo;
}

class UserService {
    api: AxiosInstance;

    constructor () {
        this.api = createApi("/b4cockpit/");
    }

    async getMyAccount (): Promise<MyAccountResponse> {
        const response = await this.api.get(CURRENT_USER);
        return response.data;
    }

    async saveMyAccount (request: ModifyAccountRequest): Promise<UniqueNo> {
        return await this.api.post(CURRENT_USER, request);
    }

    async changePassword (request: PasswordChangeRequest): Promise<void> {
        await this.api.post(USER_PASSWORD_OVERRIDE, request);
    }

    async getUserList (): Promise<UserCoversResponse> {
        return await this.api.get<UserCoversResponse>(USERS)
            .then(response => response.data);
    }

    async assembleUser (): Promise<UserDetails> {
        return await this.api.get(USER_ASSEMBLY).then(res => res.data);
    }

    async addUser (request: SaveUserRequest): Promise<void> {
        await this.api.post(USER_ADD, request)
            .catch(err => {
                enqueueSnackbar(t(
                    err.status === 409
                        ? "userDetail.error.createFailedDuplicateMail"
                        : "userDetail.error.createFailed"
                ), {
                    variant: "error",
                    autoHideDuration: 10000
                });
            throw err;
        });
    };

    async getUserDetails (userId: string): Promise<UserDetails> {
        return await this.api.get(USER_DETAILS + userId).then(res => res.data);
    }

    async getAuditTrail (userId: string): Promise<UserAuditTrail[]> {
        return await this.api.get(USER_AUDIT_TRAIL + userId).then(res => res.data);
    }

    async editUser (userId: string, request: SaveUserRequest): Promise<void> {
        await this.api.post(USER_EDIT + userId, request)
            .catch(() => {
                enqueueSnackbar(t("userDetail.error.saveFailed"), {variant: "error"});
            });
    }

    async activateUser (userId: string): Promise<void> {
        await this.api.get(USER_ACTIVATE + userId);
    }

    async deactivateUser (userId: string): Promise<void> {
        await this.api.get(USER_DEACTIVATE + userId);
    }

    async removeUser (userId: string): Promise<void> {
        await this.api.get(USER_REMOVE + userId);
    }
}

const userServiceInstance = new UserService();

export default userServiceInstance;
